import React from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import Icon from 'components/icon/icon.component';
import { ITankState } from 'interfaces/applicant.interface';
import { OIL_PRODUCT_COLORS } from 'constants/main.constants';
import { formatNumber, nominalCapacity, readingAgeMinutes } from 'utils/monitoring.utils';
import './tank-card.style.scss';

interface IProps {
  tank: ITankState;
  kpuName?: string;
  /** Minutes after which a tank is treated as having gone quiet. */
  staleAfterMinutes?: number;
}

const TankCard: React.FC<IProps> = ({ tank, kpuName, staleAfterMinutes = 10 }) => {
  const { t } = useTranslation();

  const color = (tank.oilProductType && OIL_PRODUCT_COLORS[tank.oilProductType]) ?? '#808080';
  const capacity = nominalCapacity(tank.name);
  const fillPct =
    capacity && tank.volume !== undefined
      ? Math.max(0, Math.min(100, (tank.volume / capacity) * 100))
      : null;

  const ageMinutes = readingAgeMinutes(tank.dateTime);
  const stale = ageMinutes !== null && ageMinutes > staleAfterMinutes;

  return (
    <article className={clsx('tankCard', stale && 'tankCard--stale')}>
      <header className="tankCard__head">
        <div className="tankCard__titles">
          <h3 className="tankCard__name" title={tank.name}>
            {tank.name || `#${tank.id}`}
          </h3>
          {kpuName && <span className="tankCard__kpu">{kpuName}</span>}
        </div>
        {tank.oilProductType && (
          <span className="tankCard__product" style={{ background: color }}>
            {tank.oilProductType}
          </span>
        )}
      </header>

      <div className="tankCard__gaugeRow">
        <div className="tankCard__gauge" aria-hidden="true">
          <div
            className="tankCard__gaugeFill"
            style={{ height: `${fillPct ?? 100}%`, background: color, opacity: fillPct === null ? 0.25 : 1 }}
          />
        </div>

        <div className="tankCard__primary">
          <div className="tankCard__volume">
            {formatNumber(tank.volume)}
            <span className="tankCard__unit">
              {tank.volumeUnitType && t(`enum.volumeUnitShort.${tank.volumeUnitType}`)}
            </span>
          </div>
          {/* Capacity is inferred from the tank designation (e.g. RVS-2000), so both
              numbers are shown rather than a bare percentage that could mislead. */}
          {capacity && (
            <div className="tankCard__capacity">
              {t('monitoring.ofCapacity', {
                used: formatNumber(tank.volume, 0),
                total: formatNumber(capacity, 0),
              })}
            </div>
          )}
        </div>
      </div>

      <dl className="tankCard__metrics">
        <div>
          <dt>{t('monitoring.metrics.mass')}</dt>
          <dd>
            {formatNumber(tank.mass)}{' '}
            {tank.massUnitType && t(`enum.massUnitShort.${tank.massUnitType}`)}
          </dd>
        </div>
        <div>
          <dt>{t('monitoring.metrics.prodLevel')}</dt>
          <dd>
            {formatNumber(tank.prodLevel)}{' '}
            {tank.levelUnitType && t(`enum.levelUnitShort.${tank.levelUnitType}`)}
          </dd>
        </div>
        <div>
          <dt>{t('monitoring.metrics.temp')}</dt>
          <dd>{formatNumber(tank.temp, 1)} °C</dd>
        </div>
        <div>
          <dt>{t('monitoring.metrics.prodDensity')}</dt>
          <dd>{formatNumber(tank.prodDensity, 3)}</dd>
        </div>
      </dl>

      <footer className="tankCard__foot">
        <Icon name={stale ? 'TriangleAlert' : 'Clock'} size={13} />
        <span>{tank.dateTime || '—'}</span>
        {stale && <span className="tankCard__staleTag">{t('monitoring.stale')}</span>}
      </footer>
    </article>
  );
};

export default TankCard;
